import cookie from "react-cookies";
import authConfig from "../../config/auth";

class SessionHelper {
  static KEY_USER = "user";
  static KEY_TOKEN = "token";
  // Random id of the current login, shared by all tabs. A new sign-in
  // replaces it and logout removes it, so a request or token refresh
  // started under an earlier login can tell it is stale.
  static KEY_LOGIN_ID = "login_id";
  static SESSION_EMAIL = "email";
  static SESSION_SSO = "sso";

  static currentUser() {
    return SessionHelper.getValueForKey(SessionHelper.KEY_USER);
  }

  static getToken() {
    return cookie.load(SessionHelper.KEY_TOKEN);
  }

  static setUserData(admin_data) {
    const dict = {};
    dict.admin_id = admin_data.admin_id;
    dict.admin_email = admin_data.admin_email;
    dict.admin_name = admin_data.admin_name;
    dict.admin_image = admin_data.admin_image;
    dict.admin_roles = admin_data.admin_roles || admin_data.roles || [];
    dict.permissions = admin_data.permissions || [];
    // SSO sessions set this to true so My Account knows to lock
    // the federated email. Cherry-picked here rather than spread,
    // so it has to be added explicitly when new login-side fields
    // get added.
    dict.is_federated = !!admin_data.is_federated;

    SessionHelper.setDataForKey(dict, SessionHelper.KEY_USER);
  }

  static setToken(token) {
    SessionHelper.setDataForKey(token, SessionHelper.KEY_TOKEN);
  }

  /**
   * Start a new login: drop the previous login's refresh tokens, record
   * how this one signed in (email -> APIv2 refresh, SSO -> Cognito
   * refresh) and give it a new login id. Call before storing its tokens.
   */
  static beginLogin(sessionType) {
    SessionHelper.clearTtsAuthTokens();
    localStorage.setItem('tts_session_type', sessionType);
    SessionHelper.setDataForKey(SessionHelper.newLoginId(), SessionHelper.KEY_LOGIN_ID);
  }

  static newLoginId() {
    return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  static getLoginId() {
    return SessionHelper.getValueForKey(SessionHelper.KEY_LOGIN_ID) || null;
  }

  /**
   * True when this login refreshes through Cognito (SSO). Sessions from
   * before the session type was stored fall back to is_federated.
   */
  static isSsoSession() {
    const type = localStorage.getItem('tts_session_type');
    if (type) return type === SessionHelper.SESSION_SSO;
    return SessionHelper.currentUser()?.is_federated === true;
  }

  static isLoggedIn() {
    const token = SessionHelper.getToken();
    const user = SessionHelper.currentUser();
    return token != null && user != null && user.admin_id != null;
  }

  static hasPermission(requiredPermissions) {
    const user = SessionHelper.currentUser();
    if (!user || !user.permissions) {
      return false;
    }
    // If no permissions are required, allow access
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }
    return requiredPermissions.every(permission => user.permissions.includes(permission));
  }

  static logout() {
    // Fire-and-forget API call to clear server-side permission cache and revoke refresh tokens
    const token = SessionHelper.getToken();
    if (token) {
      fetch(`${authConfig.apiV2Url}/api/admin/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }

    SessionHelper.clearTtsAuthTokens();
    localStorage.removeItem('tts_session_type');
    SessionHelper.removeDataForKey(SessionHelper.KEY_USER);
    SessionHelper.removeDataForKey(SessionHelper.KEY_TOKEN);
    SessionHelper.removeDataForKey(SessionHelper.KEY_LOGIN_ID);
  }

  static setTtsRefreshToken(refreshToken) {
    if (refreshToken) localStorage.setItem('tts_refresh_token', refreshToken);
  }

  static getTtsRefreshToken() {
    return localStorage.getItem('tts_refresh_token');
  }

  // APIv2 refresh token of an email/password login. Kept apart from the
  // Cognito one so it is never sent to TTS Auth.
  static setApiV2RefreshToken(refreshToken) {
    if (refreshToken) localStorage.setItem('apiv2_refresh_token', refreshToken);
  }

  static getApiV2RefreshToken() {
    return localStorage.getItem('apiv2_refresh_token');
  }

  static clearLegacyTtsRefreshToken() {
    localStorage.removeItem('tts_refresh_token');
  }

  /**
   * Revoke the Cognito session on the TTS Auth server and return the Cognito
   * hosted-UI logout URL. The caller should redirect the browser to this URL
   * so Cognito clears its session cookie — without this, Cognito reuses the
   * old session (and its stale claims) on the next login.
   */
  static async revokeCognitoSession(refreshToken, logoutUri) {
    try {
      const response = await fetch(`${authConfig.ttsAuthUrl}/api/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refresh_token: refreshToken,
          client_id: authConfig.clientId,
          logout_uri: logoutUri,
        }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.data?.logout_url || null;
    } catch {
      return null;
    }
  }

  static clearTtsAuthTokens() {
    // Clear from both storage types (localStorage is current, sessionStorage is legacy)
    localStorage.removeItem('tts_refresh_token');
    localStorage.removeItem('apiv2_refresh_token');
    sessionStorage.removeItem('tts_access_token');
    sessionStorage.removeItem('tts_refresh_token');
    sessionStorage.removeItem('tts_token_expires_at');
  }

  static setDataForKey(data, key) {
    cookie.save(key, data, { path: '/' });
  }

  static getValueForKey(key) {
    return cookie.load(key);
  }

  static removeDataForKey(key) {
    cookie.remove(key, { path: '/' });
  }
}

export default SessionHelper;
